import React, { lazy } from "react";

// Kept lazy, as it was in TrainerDashboard: the board is a heavy view
// that only the team scope renders.
const TeamScheduleBoard = lazy(() => import("../TeamScheduleBoard"));
import SessionCalendar from "../../components/SessionCalendar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Bell, Calendar, Check, Clock, Plus, Search, Trash2, User as UserIcon, UserX, Users, X } from "lucide-react";
import { Suspense } from "react";

// Statusy trafiające do grupy "Historia". Nieobecność też — bez niej sesja
// oznaczona jako nieobecność znikała ze wszystkich list: nie jest ani
// nadchodząca, ani oczekująca, a w historii jej nie było.
const HISTORY_STATUSES = ['completed', 'cancelled', 'rejected', 'absent'];
const HISTORY_LABELS = {
  completed: 'Zakończona',
  rejected: 'Odrzucona',
  cancelled: 'Odwołana',
  absent: 'Nieobecność',
};

// Session calendar, availability and booking management.
const CalendarTab = ({
  availabilityExceptions,
  availabilitySlots,
  calendarScope,
  calendarSubTab,
  dayLabels,
  deleteAvailabilitySlot,
  deleteException,
  deleteLeave,
  deleteSession,
  filteredSessions,
  firmLocations,
  handleCalendarAction,
  isBusinessOrg,
  leaveSummary,
  leaveTypeLabels,
  openNewSession,
  openReassign,
  searchSessions,
  sessionTypeLabels,
  sessions,
  sessionsView,
  setAvailSlotOpen,
  setCalendarScope,
  setCalendarSubTab,
  setExceptionOpen,
  setLeaveOpen,
  setNewAvailSlot,
  setNewException,
  setNewLeave,
  setSearchSessions,
  setSessionsView,
  t,
  token,
  trainerLeaves,
  sessionAction,
  rejectSession,
  cancelSession,
  markAbsent,
}) => {
  return (
    <div className="calendar-view">
      <div className="view-header">
        <h1>{t("calendar.title")}</h1>
        <div style={{display:'flex', gap:'8px', alignItems:'center'}}>
          {calendarScope === 'mine' && calendarSubTab === 'sessions' && (
            <>
              <div style={{position:'relative'}}>
                <Search size={16} style={{position:'absolute',left:8,top:'50%',transform:'translateY(-50%)',color:'var(--text-secondary)'}} />
                <Input placeholder="Szukaj klienta..." value={searchSessions} onChange={e => setSearchSessions(e.target.value)} style={{paddingLeft:32,width:200}} />
              </div>
              <div style={{display:'flex', gap:2, background:'var(--bg-secondary)', borderRadius:8, padding:3}}>
                {[['month','Miesiąc'],['week','Tydzień'],['list','Lista']].map(([v,label]) => (
                  <button key={v} type="button" onClick={() => setSessionsView(v)}
                    style={{padding:'6px 12px', borderRadius:6, border:'none', cursor:'pointer', fontSize:13,
                      fontWeight: sessionsView===v ? 600 : 400,
                      background: sessionsView===v ? 'var(--bg-primary)' : 'transparent',
                      color:'var(--text-primary)', transition:'all 0.2s'}}>
                    {label}
                  </button>
                ))}
              </div>
              <Button onClick={() => openNewSession()}>
                <Plus size={18} /> Nowa sesja
              </Button>
            </>
          )}
          {calendarScope === 'mine' && calendarSubTab === 'availability' && (
            <div style={{display:'flex',gap:8}}>
              <Button onClick={() => { setNewAvailSlot({ day_of_week: 0, slots: [{ start_time: "09:00", end_time: "10:00" }], location: "", location_ids: [], max_clients_per_slot: 1 }); setAvailSlotOpen(true); }}>
                <Plus size={18} /> Dodaj godziny
              </Button>
              <Button variant="outline" onClick={() => { setNewException({ date: "", available: false, reason: "" }); setExceptionOpen(true); }}>
                <X size={18} /> Wolne / Blokada
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Scope toggle: my calendar vs whole team (firm members only) */}
      {isBusinessOrg && (
        <div style={{display:'flex', gap:4, marginBottom:12, background:'var(--bg-secondary)', borderRadius:8, padding:4}}>
          {[['mine','Moje'],['team','Zespół']].map(([v,label]) => (
            <button key={v} type="button"
              onClick={() => { setCalendarScope(v); if (v === 'team' && sessionsView === 'list') setSessionsView('month'); }}
              style={{flex:1, padding:'8px 16px', borderRadius:6, border:'none', cursor:'pointer', fontSize:14,
                fontWeight: calendarScope===v ? 600 : 400,
                background: calendarScope===v ? 'var(--bg-primary)' : 'transparent',
                color:'var(--text-primary)', transition:'all 0.2s'}}>
              {v === 'team' ? <Users size={15} style={{marginRight:6, verticalAlign:'middle'}} /> : <UserIcon size={15} style={{marginRight:6, verticalAlign:'middle'}} />}
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Team scope — rich filterable schedule board (who is free, where, when) */}
      {calendarScope === 'team' && (
        <Suspense fallback={<div style={{padding:'32px',color:'var(--text-secondary)'}}>Ladowanie...</div>}>
          <TeamScheduleBoard token={token} />
        </Suspense>
      )}

      {/* Sub-tab toggle (own calendar) */}
      {calendarScope === 'mine' && (
      <div style={{display:'flex', gap:4, marginBottom:16, background:'var(--bg-secondary)', borderRadius:8, padding:4}}>
        <button
          className={`nav-btn ${calendarSubTab === 'sessions' ? 'active' : ''}`}
          style={{flex:1, padding:'8px 16px', borderRadius:6, border:'none', cursor:'pointer', fontWeight:calendarSubTab === 'sessions' ? 600 : 400, background:calendarSubTab === 'sessions' ? 'var(--bg-primary)' : 'transparent', color:'var(--text-primary)', transition:'all 0.2s'}}
          onClick={() => setCalendarSubTab('sessions')}
        >
          <Calendar size={16} style={{marginRight:6, verticalAlign:'middle'}} />
          Sesje {sessions.filter(s => s.status === 'scheduled' || s.status === 'pending_confirmation').length > 0 && <Badge variant="secondary" style={{marginLeft:4}}>{sessions.filter(s => s.status === 'scheduled' || s.status === 'pending_confirmation').length}</Badge>}
        </button>
        <button
          className={`nav-btn ${calendarSubTab === 'availability' ? 'active' : ''}`}
          style={{flex:1, padding:'8px 16px', borderRadius:6, border:'none', cursor:'pointer', fontWeight:calendarSubTab === 'availability' ? 600 : 400, background:calendarSubTab === 'availability' ? 'var(--bg-primary)' : 'transparent', color:'var(--text-primary)', transition:'all 0.2s'}}
          onClick={() => setCalendarSubTab('availability')}
        >
          <Clock size={16} style={{marginRight:6, verticalAlign:'middle'}} />
          Dostępność
        </button>
      </div>
      )}

      {/* Sessions sub-tab — calendar (month/week), own sessions */}
      {calendarScope === 'mine' && calendarSubTab === 'sessions' && sessionsView !== 'list' && (
        <SessionCalendar
          sessions={filteredSessions}
          sessionTypeLabels={sessionTypeLabels}
          view={sessionsView === 'week' ? 'week' : 'month'}
          onNewSession={openNewSession}
          onEventAction={handleCalendarAction}
        />
      )}

      {/* Sessions sub-tab — list (own only) */}
      {calendarSubTab === 'sessions' && calendarScope === 'mine' && sessionsView === 'list' && (
        <>
          {/* Pending confirmation bookings */}
          {sessions.filter(s => s.status === 'pending_confirmation').length > 0 && (
            <div className="session-group" style={{marginBottom:20}}>
              <h2 style={{display:'flex',alignItems:'center',gap:8}}>
                <Bell size={18} style={{color:'var(--warning)'}} />
                Oczekujące na potwierdzenie
                <Badge variant="destructive">{sessions.filter(s => s.status === 'pending_confirmation').length}</Badge>
              </h2>
              <div className="sessions-list">
                {sessions.filter(s => s.status === 'pending_confirmation').map(s => (
                  <Card key={s.id} className="session-card" style={{borderLeft:'3px solid var(--warning)'}}>
                    <CardContent>
                      <div className="session-date-col">
                        <span className="session-day">{new Date(s.date + 'T00:00').toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                        <span className="session-time">{s.time}</span>
                      </div>
                      <div className="session-info">
                        <h3>{s.client_name}</h3>
                        <div className="session-meta">
                          <Badge variant="outline">{sessionTypeLabels[s.type] || s.type}</Badge>
                          <span>{s.duration} min</span>
                        </div>
                        {s.notes && <p style={{fontSize:12,color:'var(--text-secondary)',margin:'4px 0 0'}}>{s.notes}</p>}
                      </div>
                      <div className="session-actions">
                        <Button variant="default" size="sm" onClick={() => sessionAction(s.id, 'confirm')}>
                          <Check size={14} /> Potwierdź
                        </Button>
                        <Button variant="destructive" size="sm" onClick={() => rejectSession(s.id)}>
                          <X size={14} /> Odrzuć
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {filteredSessions.length === 0 ? (
            <Card className="empty-state">
              <CardContent>
                <Calendar size={48} strokeWidth={1} />
                <h3>Brak sesji</h3>
                <p>Zaplanuj sesje treningowe z klientami</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {filteredSessions.filter(s => s.status === 'scheduled' || s.status === 'confirmed').length > 0 && (
                <div className="session-group">
                  <h2>Nadchodzące</h2>
                  <div className="sessions-list">
                    {filteredSessions.filter(s => s.status === 'scheduled' || s.status === 'confirmed').map(s => (
                      <Card key={s.id} className="session-card">
                        <CardContent>
                          <div className="session-date-col">
                            <span className="session-day">{new Date(s.date + 'T00:00').toLocaleDateString('pl-PL', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                            <span className="session-time">{s.time}</span>
                          </div>
                          <div className="session-info">
                            <h3>{s.client_name}</h3>
                            <div className="session-meta">
                              <Badge variant="outline">{sessionTypeLabels[s.type] || s.type}</Badge>
                              <span>{s.duration} min</span>
                              {s.status === 'confirmed' && <Badge variant="default">Potwierdzona</Badge>}
                            </div>
                          </div>
                          <div className="session-actions">
                            <Button variant="default" size="sm" title="Zakończ sesję" onClick={() => sessionAction(s.id, 'complete')}>
                              <Check size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Nieobecność klienta" onClick={() => markAbsent(s.id)}>
                              <UserX size={14} />
                            </Button>
                            <Button variant="outline" size="sm" title="Odwołaj sesję" onClick={() => cancelSession(s.id)}>
                              <X size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" title="Zastąp trenera" onClick={() => openReassign(s)}>
                              <Users size={14} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteSession(s.id)}>
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
              {filteredSessions.filter(s => HISTORY_STATUSES.includes(s.status)).length > 0 && (
                <div className="session-group">
                  <h2>Historia</h2>
                  <div className="sessions-list">
                    {filteredSessions.filter(s => HISTORY_STATUSES.includes(s.status))
                      // Sesje przychodzą posortowane rosnąco, więc samo slice(0, 20)
                      // pokazywało dwadzieścia NAJSTARSZYCH — czyli w sekcji
                      // "Historia" nie było widać tego, co wydarzyło się ostatnio.
                      .slice().reverse().slice(0, 20).map(s => (
                      <Card key={s.id} className={`session-card session-${s.status}`}>
                        <CardContent>
                          <div className="session-date-col">
                            <span className="session-day">{new Date(s.date + 'T00:00').toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}</span>
                            <span className="session-time">{s.time}</span>
                          </div>
                          <div className="session-info">
                            <h3>{s.client_name}</h3>
                            <Badge variant={s.status === 'completed' ? 'default' : s.status === 'absent' ? 'secondary' : 'destructive'}>
                              {HISTORY_LABELS[s.status] || s.status}
                            </Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Availability sub-tab — own weekly hours editor */}
      {calendarScope === 'mine' && calendarSubTab === 'availability' && (
        <>
          {/* Weekly availability grid */}
          <div style={{marginBottom:24}}>
            <h2 style={{marginBottom:12}}>Stałe godziny pracy</h2>
            {availabilitySlots.length === 0 ? (
              <Card className="empty-state">
                <CardContent>
                  <Clock size={48} strokeWidth={1} />
                  <h3>Brak ustawionych godzin</h3>
                  <p>Dodaj godziny dostępności, aby klienci mogli rezerwować sesje</p>
                </CardContent>
              </Card>
            ) : (
              <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(280px, 1fr))', gap:12}}>
                {availabilitySlots.map(avail => (
                  <Card key={avail.id}>
                    <CardContent style={{padding:16}}>
                      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8}}>
                        <h3 style={{margin:0, fontSize:15, fontWeight:600}}>{dayLabels[avail.day_of_week]}</h3>
                        <Button variant="ghost" size="sm" onClick={() => deleteAvailabilitySlot(avail.id)}>
                          <Trash2 size={14} />
                        </Button>
                      </div>
                      <div style={{display:'flex', flexWrap:'wrap', gap:6}}>
                        {avail.slots.map((s, i) => (
                          <Badge key={i} variant="outline" style={{fontSize:13, padding:'4px 10px'}}>
                            {s.start_time} – {s.end_time}
                          </Badge>
                        ))}
                      </div>
                      {(avail.location_ids && avail.location_ids.length > 0) ? (
                        <p style={{fontSize:12, color:'var(--text-secondary)', marginTop:6}}>
                          📍 {avail.location_ids.map((id) => (firmLocations.find((l) => l.id === id) || {}).name || id).join(", ")}
                        </p>
                      ) : avail.location ? (
                        <p style={{fontSize:12, color:'var(--text-secondary)', marginTop:6}}>📍 {avail.location}</p>
                      ) : null}
                      {avail.max_clients_per_slot > 1 && <p style={{fontSize:12, color:'var(--text-secondary)', marginTop:4}}>Maks. {avail.max_clients_per_slot} os./slot</p>}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Leaves / L4 */}
          <div style={{marginBottom:24}}>
            <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12}}>
              <h2>{t("trainer.leave.title")}</h2>
              <Button size="sm" onClick={() => { setNewLeave({ date_from: "", date_to: "", type: "vacation", reason: "", document_url: "" }); setLeaveOpen(true); }}>
                <Plus size={14} /> {t("trainer.leave.add")}
              </Button>
            </div>

            {/* Summary */}
            {leaveSummary && leaveSummary.total_days > 0 && (
              <div style={{display:'flex', gap:8, marginBottom:12, flexWrap:'wrap'}}>
                <Badge variant="outline" style={{fontSize:12, padding:'4px 10px'}}>
                  Łącznie: {leaveSummary.total_days} {t("trainer.leave.days")}
                </Badge>
                {Object.entries(leaveSummary.by_type || {}).map(([type, days]) => (
                  <Badge key={type} variant="secondary" style={{fontSize:11, padding:'3px 8px'}}>
                    {leaveTypeLabels[type] || type}: {days}d
                  </Badge>
                ))}
              </div>
            )}

            {trainerLeaves.length === 0 ? (
              <p style={{color:'var(--text-secondary)', fontSize:14}}>{t("trainer.leave.noLeaves")}</p>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:8}}>
                {trainerLeaves.map(leave => (
                  <Card key={leave.id}>
                    <CardContent style={{padding:12, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                      <div style={{display:'flex', alignItems:'center', gap:12, flexWrap:'wrap'}}>
                        <Badge variant={leave.type === 'sick_leave' ? 'destructive' : leave.type === 'vacation' ? 'default' : 'secondary'}>
                          {leaveTypeLabels[leave.type] || leave.type}
                        </Badge>
                        <span style={{fontWeight:500}}>
                          {new Date(leave.date_from + 'T00:00').toLocaleDateString('pl-PL', { day: 'numeric', month: 'short' })}
                          {' — '}
                          {new Date(leave.date_to + 'T00:00').toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </span>
                        <span style={{fontSize:12, color:'var(--text-secondary)'}}>({leave.days} {t("trainer.leave.days")})</span>
                        {leave.reason && <span style={{fontSize:13, color:'var(--text-secondary)'}}>— {leave.reason}</span>}
                        {leave.document_url && (
                          <a href={leave.document_url} target="_blank" rel="noopener noreferrer" style={{fontSize:12, color:'var(--primary)'}}>📄 Dokument</a>
                        )}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteLeave(leave.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>

          {/* Exceptions */}
          <div>
            <h2 style={{marginBottom:12}}>Wyjątki i dni wolne</h2>
            {availabilityExceptions.length === 0 ? (
              <p style={{color:'var(--text-secondary)', fontSize:14}}>Brak wyjątków — klienci mogą rezerwować zgodnie ze stałym grafikiem.</p>
            ) : (
              <div style={{display:'flex', flexDirection:'column', gap:8}}>
                {availabilityExceptions.map(ex => (
                  <Card key={ex.id}>
                    <CardContent style={{padding:12, display:'flex', alignItems:'center', justifyContent:'space-between'}}>
                      <div style={{display:'flex', alignItems:'center', gap:12}}>
                        <Badge variant={ex.available ? 'default' : 'destructive'}>
                          {ex.available ? 'Dodatkowe godziny' : 'Dzień wolny'}
                        </Badge>
                        <span style={{fontWeight:500}}>{new Date(ex.date + 'T00:00').toLocaleDateString('pl-PL', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
                        {ex.reason && <span style={{fontSize:13, color:'var(--text-secondary)'}}>— {ex.reason}</span>}
                      </div>
                      <Button variant="ghost" size="sm" onClick={() => deleteException(ex.id)}>
                        <Trash2 size={14} />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default CalendarTab;
